import {Button, Menu} from '@mantine/core';
import {MdExpandMore} from 'react-icons/md';
import {MANAGEMENT_OVERALL_STATUS_VALUES} from '../../../../../../core/entities/entryManagements/constants';
import type {ManagementOverAllStatusType} from '../../../../../../core/entities/entryManagements/schema';
import {cn} from '../../../../../../lib/classname';

type Props = {
  /** 現在の全体ステータス（active/completed/archived/rejected） */
  currentStatus: ManagementOverAllStatusType;
  /** 内定獲得ステータスに変更するハンドラー */
  onCompleteClick?: () => void;
  /** 辞退ステータスに変更するハンドラー */
  onArchiveClick?: () => void;
  /** 落選ステータスに変更するハンドラー */
  onRejectedClick?: () => void;
  /** エントリー完了状態（true: エントリー済み, false: 未エントリー） */
  hasCompletedEntry: boolean;
  /** 未エントリー状態に変更（hasCompletedEntry: false + overallStatus: active） */
  onChangeHasCompletedEntryFalse?: () => void;
  /** エントリー済み状態に変更（hasCompletedEntry: true + overallStatus: active） */
  onChangeHasCompletedEntryTrue?: () => void;
  /** ボタンの無効化状態 */
  disabled?: boolean;
};

/**
 * 全体ステータスとエントリー完了状態に応じたボタンラベルを取得
 * @param status - 全体ステータス（active/completed/archived/rejected）
 * @param hasCompletedEntry - エントリー完了状態（activeステータス時のみ使用）
 * @returns ボタンに表示するラベル文字列
 */
const getButtonLabel = (
  status: ManagementOverAllStatusType,
  hasCompletedEntry: boolean,
): string => {
  switch (status) {
    case MANAGEMENT_OVERALL_STATUS_VALUES.active:
      return hasCompletedEntry ? 'エントリー済み' : '未エントリー';
    case MANAGEMENT_OVERALL_STATUS_VALUES.completed:
      return '内定獲得';
    case MANAGEMENT_OVERALL_STATUS_VALUES.archived:
      return '辞退';
    case MANAGEMENT_OVERALL_STATUS_VALUES.rejected:
      return '落選';
    default:
      return '';
  }
};

/**
 * 全体ステータス変更ボタンコンポーネント
 * ドロップダウンメニューから以下のステータスに変更できる：
 * - 未エントリー（hasCompletedEntry: false, overallStatus: active）
 * - エントリー済み（hasCompletedEntry: true, overallStatus: active）
 * - 内定獲得（overallStatus: completed）
 * - 落選（overallStatus: rejected）
 * - 辞退（overallStatus: archived）
 *
 * レース条件を回避するため、未エントリー/エントリー済みへの変更は
 * hasCompletedEntryとoverallStatusを1回のAPI呼び出しで更新する
 */
export const OverallStatusChangeButton: React.FC<Props> = ({
  currentStatus,
  onCompleteClick,
  onArchiveClick,
  onRejectedClick,
  hasCompletedEntry,
  onChangeHasCompletedEntryFalse,
  onChangeHasCompletedEntryTrue,
  disabled = false,
}) => {
  const buttonLabel = getButtonLabel(currentStatus, hasCompletedEntry);

  return (
    <Menu shadow="md" width={160}>
      <Menu.Target>
        <Button
          disabled={disabled}
          rightIcon={<MdExpandMore size={18} />}
          variant="outline"
          className={cn('font-bold text-sm rounded-full', {
            'border-success-1 bg-success-1 text-white hover:!bg-success-1 hover:opacity-80':
              currentStatus === MANAGEMENT_OVERALL_STATUS_VALUES.completed,
            'border-black-3 bg-black-3 text-white hover:!bg-black-3 hover:opacity-80':
              currentStatus === MANAGEMENT_OVERALL_STATUS_VALUES.archived ||
              currentStatus === MANAGEMENT_OVERALL_STATUS_VALUES.rejected,
            'border-primary bg-white text-primary hover:!bg-white hover:opacity-80':
              currentStatus === MANAGEMENT_OVERALL_STATUS_VALUES.active,
          })}
        >
          {buttonLabel}
        </Button>
      </Menu.Target>

      <Menu.Dropdown>
        <Menu.Item onClick={onChangeHasCompletedEntryFalse}>
          未エントリー
        </Menu.Item>
        <Menu.Item onClick={onChangeHasCompletedEntryTrue}>
          エントリー済み
        </Menu.Item>
        <Menu.Item onClick={onCompleteClick}>内定獲得</Menu.Item>
        <Menu.Item onClick={onRejectedClick}>落選</Menu.Item>
        <Menu.Item onClick={onArchiveClick}>辞退</Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
};
